import axios from 'axios';
import { ensureFreshAitryxCoreToken } from './aitryxCoreTokenService';
import { config } from '../config/environment';

// Marketplace API URL from centralized config
const MARKETPLACE_API_URL = config.api.marketplaceUrl;

/**
 * Distributor configuration status
 */
export interface DistributorConfig {
  apiToken: string | null;
  lastSyncAt: string | null;
  lastSyncStatus: string | null;
  lastSyncError: string | null;
  productCount: number | null;
}

/**
 * Product from distributor cache
 */
export interface DistributorProduct {
  sku: string;
  brand: string | null;
  description: string | null;
  unitPrice: number | null;  // Buy price from distributor
  rrpExTax: number | null;   // RRP for reference
  soh: number | null;
  lastUpdated: string;
  category?: string | null;
}

/**
 * Distributor products response
 */
export interface DistributorProductsResponse {
  fetchedAt: string | null;
  products: DistributorProduct[];
}

/**
 * Sync result from distributor
 */
export interface SyncResult {
  success: boolean;
  productsUpdated: number;
  productsFailed: number;
  /**
   * Count of NEW SKUs whose AI enrichment was actually queued by this sync
   * (omitted by older marketplace builds — treat undefined as "unknown" and
   * fall back to a single post-sync refresh). The trigger endpoint returns
   * the moment messages are on the queue; the worker takes ~10–15s per SKU,
   * so the UI uses this count to drive a poll-and-refresh loop instead of
   * leaving stale "Pending sync" badges in place until the user F5's.
   */
  enrichmentsEnqueued?: number;
  error: string | null;
  syncedAt: string;
}

/**
 * Seed SKU for pre-scoped products
 */
export interface SeedSku {
  sku: string;
  category: string | null;
  name: string | null;
  enabled: boolean;
}

/**
 * Seed SKU configuration
 */
export interface SeedSkuConfig {
  createdAt: string;
  updatedAt: string | null;
  skus: SeedSku[];
}

/**
 * Connection test result
 */
export interface ConnectionTestResult {
  success: boolean;
  error: string | null;
  message: string;
}

/**
 * Creates an authenticated axios instance for the marketplace API
 */
async function getAuthenticatedClient() {
  const token = await ensureFreshAitryxCoreToken();
  return axios.create({
    baseURL: MARKETPLACE_API_URL,
    timeout: 60000,
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    }
  });
}

/**
 * Get distributor configuration for the current partner
 */
export async function getDistributorConfig(): Promise<DistributorConfig> {
  try {
    const client = await getAuthenticatedClient();
    const response = await client.get('/distributor/config');
    return response.data;
  } catch (error: any) {

    throw new Error(error.response?.data?.error || 'Failed to get distributor configuration');
  }
}

/**
 * Save distributor API key for the current partner
 */
export async function saveDistributorConfig(apiToken: string): Promise<void> {
  try {
    const client = await getAuthenticatedClient();
    await client.put('/distributor/config', { apiToken });
    // Notify the app shell to refresh global integration health banners immediately.
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new Event('aitryx:distributor-health-refresh'));
    }
  } catch (error: any) {

    throw new Error(error.response?.data?.error || 'Failed to save distributor configuration');
  }
}

/**
 * Test distributor API connection
 */
export async function testDistributorConnection(apiToken: string): Promise<ConnectionTestResult> {
  try {
    const client = await getAuthenticatedClient();
    const response = await client.post('/distributor/test', { apiToken });
    return response.data;
  } catch (error: any) {

    return {
      success: false,
      error: error.response?.data?.error || 'Connection test failed',
      message: 'Connection test failed'
    };
  }
}

/**
 * Trigger manual sync for distributor products.
 *
 * `distributorId` defaults to undefined (server falls back to dickerdata for
 * back-compat). Pass 'ingram' etc. to sync other connectors.
 */
export async function triggerDistributorSync(distributorId?: string): Promise<SyncResult> {
  try {
    const client = await getAuthenticatedClient();
    const url = distributorId
      ? `/distributor/sync?distributor=${encodeURIComponent(distributorId)}`
      : '/distributor/sync';
    const response = await client.post(url);
    // Sync success/failure should reflect immediately in global integration health.
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new Event('aitryx:distributor-health-refresh'));
    }
    return response.data;
  } catch (error: any) {

    throw new Error(error.response?.data?.error || 'Failed to trigger sync');
  }
}

/**
 * Get cached distributor products
 */
export async function getDistributorProducts(): Promise<DistributorProductsResponse> {
  try {
    const client = await getAuthenticatedClient();
    const response = await client.get('/distributor/products');
    return response.data;
  } catch (error: any) {

    throw new Error(error.response?.data?.error || 'Failed to get distributor products');
  }
}

/**
 * Get seed SKUs configuration
 */
export async function getSeedSkus(): Promise<SeedSkuConfig> {
  try {
    const client = await getAuthenticatedClient();
    const response = await client.get('/distributor/skus/seed');
    return response.data;
  } catch (error: any) {

    throw new Error(error.response?.data?.error || 'Failed to get seed SKUs');
  }
}

/**
 * Save seed SKUs configuration
 */
export async function saveSeedSkus(skus: SeedSku[]): Promise<void> {
  try {
    const client = await getAuthenticatedClient();
    await client.post('/distributor/skus/seed', skus);
  } catch (error: any) {

    throw new Error(error.response?.data?.error || 'Failed to save seed SKUs');
  }
}

/**
 * Import result from catalogue import
 */
export interface ImportResult {
  success: boolean;
  importedCount: number;
  updatedCount: number;
  error: string | null;
}

/**
 * Import options for pricing configuration
 */
export interface ImportOptions {
  /** Markup percentage to apply to all products (e.g., 18 for 18%) */
  markupPercent?: number;
  /** Domain for procurement routing - required for all imported products */
  domain?: 'software' | 'computers' | 'networking' | 'mobile-hardware' | 'consulting' | 'telstra' | 'unified-collaboration';
}

const DEFAULT_DISTRIBUTOR_MARKUP_PERCENT = 18;

/**
 * Import distributor products to partner catalogue
 */
export async function importProductsToCatalogue(
  products: DistributorProduct[], 
  options?: ImportOptions
): Promise<ImportResult> {
  try {
    const client = await getAuthenticatedClient();
    const response = await client.post('/catalog/import', { 
      products: products.map(p => ({
        sku: p.sku,
        brand: p.brand,
        description: p.description,
        unitPrice: p.unitPrice,
        rrpExTax: p.rrpExTax,
        soh: p.soh,
        source: 'dickerdata',
        category: p.category
      })),
      markupPercent: options?.markupPercent ?? DEFAULT_DISTRIBUTOR_MARKUP_PERCENT,
      domain: options?.domain || 'computers'
    });
    return response.data;
  } catch (error: any) {

    throw new Error(error.response?.data?.error || 'Failed to import products');
  }
}

/**
 * Delete products from the distributor cache
 * (They will be re-synced on next sync if still in tracked SKUs)
 */
export async function deleteDistributorProducts(skus: string[]): Promise<void> {
  try {
    const client = await getAuthenticatedClient();
    await client.delete('/distributor/products', { data: { skus } });
  } catch (error: any) {

    throw new Error(error.response?.data?.error || 'Failed to delete products');
  }
}

/**
 * Sync price change record
 */
export interface SyncPriceChange {
  sku: string;
  productName: string;
  oldBuyPrice: number;
  newBuyPrice: number;
  oldSellPrice: number;
  newSellPrice: number;
  markupPercent?: number;
}

/**
 * Sync stock change record
 */
export interface SyncStockChange {
  sku: string;
  productName: string;
  oldSoh: number | null;
  newSoh: number | null;
}

/**
 * Sync history entry
 */
export interface SyncHistoryEntry {
  id: string;
  syncedAt: string;
  source: string;
  triggerType: 'manual' | 'scheduled';
  productsUpdated: number;
  productsFailed: number;
  success: boolean;
  error: string | null;
  priceChanges: SyncPriceChange[];
  stockChanges: SyncStockChange[];
}

/**
 * Sync history response
 */
export interface SyncHistoryResponse {
  createdAt: string;
  updatedAt: string | null;
  entries: SyncHistoryEntry[];
}

/**
 * Get sync history for the current partner
 */
export async function getSyncHistory(): Promise<SyncHistoryResponse> {
  try {
    const client = await getAuthenticatedClient();
    const response = await client.get('/distributor/sync/history');
    return response.data;
  } catch (error: any) {

    throw new Error(error.response?.data?.error || 'Failed to get sync history');
  }
}

// ---------------------------------------------------------------------------
// Ingram Micro — Phase 2 connector endpoints.
//
// Ingram's richer credential shape (client id + secret + customer number +
// country) doesn't fit the single-apiToken `/distributor/config` Dicker
// endpoint, so the backend exposes a parallel /distributor/ingram/* triplet
// and we mirror it here rather than overloading the existing functions.
// ---------------------------------------------------------------------------

/**
 * Stored Ingram configuration (read side). Secrets are never returned —
 * the backend masks them as `***configured***` when they're present in
 * Key Vault.
 */
export interface IngramConfig {
  connected: boolean;
  status: string | null;
  displayName: string | null;
  clientId: string | null;
  clientSecret: string | null;
  customerNumber: string | null;
  countryCode: string | null;
  senderId: string | null;
  lastTestedAt: string | null;
  lastTestResult: string | null;
}

/**
 * Inputs for save + test. Send empty / sentinel values for the secret fields
 * to leave the stored values untouched.
 */
export interface IngramConfigInput {
  clientId?: string;
  clientSecret?: string;
  customerNumber: string;
  countryCode: string;
  senderId?: string;
  displayName?: string;
}

/**
 * Richer connection test result shape for Ingram (includes latency).
 */
export interface IngramConnectionTestResult {
  success: boolean;
  latencyMs: number;
  error: string | null;
  message: string;
}

export async function getIngramConfig(): Promise<IngramConfig> {
  try {
    const client = await getAuthenticatedClient();
    const response = await client.get('/distributor/ingram/config');
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.error || 'Failed to get Ingram configuration');
  }
}

export async function saveIngramConfig(input: IngramConfigInput): Promise<void> {
  try {
    const client = await getAuthenticatedClient();
    await client.put('/distributor/ingram/config', input);
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new Event('aitryx:distributor-health-refresh'));
    }
  } catch (error: any) {
    throw new Error(error.response?.data?.error || 'Failed to save Ingram configuration');
  }
}

export async function testIngramConnection(input: IngramConfigInput): Promise<IngramConnectionTestResult> {
  try {
    const client = await getAuthenticatedClient();
    const response = await client.post('/distributor/ingram/test', input);
    return response.data;
  } catch (error: any) {
    return {
      success: false,
      latencyMs: 0,
      error: error.response?.data?.error || 'Connection test failed',
      message: 'Connection test failed',
    };
  }
}

export async function deleteIngramConfig(): Promise<void> {
  try {
    const client = await getAuthenticatedClient();
    await client.delete('/distributor/ingram/config');
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new Event('aitryx:distributor-health-refresh'));
    }
  } catch (error: any) {
    throw new Error(error.response?.data?.error || 'Failed to remove Ingram configuration');
  }
}

// ---------------------------------------------------------------------------
// Phase 3.5a — Enrichment review.
//
// LLM-generated enrichment for distributor SKUs lands as Status='Draft' and
// must be explicitly approved (with optional inline edits) before catalog
// consumers see it. Endpoints are distributor-scoped so the same UI shell
// can mount on the Dicker / Ingram / Telstra tabs.
// ---------------------------------------------------------------------------

export interface ProductEnrichmentCategory {
  primary: string | null;
  secondary: string | null;
}

/**
 * Persisted enrichment payload — matches the C# ProductEnrichment in
 * Aitryx.Marketplace.Models.Enrichment. Permissive on extras: Phase 4 may
 * extend the schema, partner UI should pass through unknown fields verbatim
 * when sending edits back.
 */
export interface ProductEnrichment {
  canonicalName: string | null;
  marketingSummary: string | null;
  productType: string | null;
  category: ProductEnrichmentCategory | null;
  attributes: Record<string, unknown>;
  imageCandidates: string[];
  // Partner-chosen image URL — sourced via review-modal paste (v1) or
  // picked from imageCandidates (v1.5). Catalog browse + storefront
  // render this. Optional for back-compat with pre-3.7 enrichments.
  imageUrl?: string | null;
  datasheetUrl: string | null;
  manufacturerWebsite: string | null;
  confidence: 'high' | 'medium' | 'low' | string | null;
}

export interface EnrichmentSummary {
  sku: string;
  status: 'Draft' | 'Active' | 'Rejected' | string;
  enrichedAt: string;
  agentVersion: string | null;
  blobPath: string;
  // Hydrated server-side from each enrichment blob — drives the new
  // Enrichment review queue list (vendor chip + confidence bar +
  // canonical-name preview) without per-row N+1 fetches. Optional
  // for backward compat with pre-hydration enrichments.
  canonicalName?: string | null;
  vendor?: string | null;
  category?: string | null;
  confidence?: 'high' | 'medium' | 'low' | string | null;
}

export interface EnrichmentListResponse {
  distributorId: string;
  status: string | null;
  count: number;
  items: EnrichmentSummary[];
}

export interface EnrichmentSourceProduct {
  sku: string;
  vendorPartNumber: string | null;
  vendor: string | null;
  shortDescription: string | null;
  displayDescription: string | null;
  category: { l1: string | null; l2: string | null; l3: string | null } | null;
  kind: string | null;
  lifecycle: string | null;
  /**
   * Partner-only raw payload from the distributor detail endpoint. Used in
   * edit/review surfaces for fact-checking; never written to customer catalogs.
   */
  rawApiResponse?: unknown;
}

export interface EnrichmentLivePriceAndStock {
  price: number | null;
  rrp: number | null;
  stock: number | null;
  fetchedAt: string;
}

export interface EnrichmentDetail {
  sku: string;
  distributorId: string;
  status: string;
  enrichedAt: string;
  agentVersion: string | null;
  blobPath: string;
  enrichment: ProductEnrichment | null;
  // Lazy-fetched raw distributor product, surfaced on GetEnrichment so the
  // Enrichment review can render side-by-side raw-vs-AI comparison cards.
  // Null when the connector lookup failed (Ingram throttled, SKU pulled
  // from catalogue, etc.) — UI falls back to "raw data unavailable".
  source?: EnrichmentSourceProduct | null;
  // Lazy-fetched live price + stock at view time. Null on connector failure.
  priceAndStock?: EnrichmentLivePriceAndStock | null;
}

export async function listEnrichments(
  distributorId: string,
  status?: 'Draft' | 'Active' | 'Rejected',
): Promise<EnrichmentListResponse> {
  try {
    const client = await getAuthenticatedClient();
    const url = status
      ? `/distributor/${encodeURIComponent(distributorId)}/enrichments?status=${encodeURIComponent(status)}`
      : `/distributor/${encodeURIComponent(distributorId)}/enrichments`;
    const response = await client.get(url);
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.error || 'Failed to list enrichments');
  }
}

export async function getEnrichment(
  distributorId: string,
  sku: string,
  options?: {
    /**
     * When false, the server skips the live distributor catalog + price/stock
     * round-trips and serves price/stock from the partner's cache (the data
     * the most recent sync wrote). Used by the Synced Products detail pane
     * where the partner has already approved/seen the SKU and doesn't need
     * a fresh upstream pull on every row click. Defaults to true to preserve
     * the Enrichment-review side-by-side comparison behaviour.
     */
    live?: boolean;
  },
): Promise<EnrichmentDetail> {
  try {
    const client = await getAuthenticatedClient();
    const qs = options?.live === false ? '?live=false' : '';
    const response = await client.get(
      `/distributor/${encodeURIComponent(distributorId)}/enrichments/${encodeURIComponent(sku)}${qs}`,
    );
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.error || 'Failed to load enrichment');
  }
}

export async function approveEnrichment(
  distributorId: string,
  sku: string,
  edits?: ProductEnrichment,
): Promise<{ status: string; noOp?: boolean }> {
  try {
    const client = await getAuthenticatedClient();
    const body = edits ? { enrichment: edits } : {};
    const response = await client.post(
      `/distributor/${encodeURIComponent(distributorId)}/enrichments/${encodeURIComponent(sku)}/approve`,
      body,
    );
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.error || 'Failed to approve enrichment');
  }
}

// ---------------------------------------------------------------------------
// Phase 3.5b — Per-distributor seed SKU lists.
// Reads/writes /distributor/{distributorId}/skus/seed. The backend only uses
// the legacy shared seed list as a Dicker Data migration fallback; newer
// distributors do not inherit that list.
// ---------------------------------------------------------------------------

export async function getDistributorSeedSkus(distributorId: string): Promise<SeedSkuConfig> {
  try {
    const client = await getAuthenticatedClient();
    const response = await client.get(
      `/distributor/${encodeURIComponent(distributorId)}/skus/seed`,
    );
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.error || 'Failed to load seed SKUs');
  }
}

export async function saveDistributorSeedSkus(
  distributorId: string,
  skus: SeedSku[],
): Promise<void> {
  try {
    const client = await getAuthenticatedClient();
    await client.post(
      `/distributor/${encodeURIComponent(distributorId)}/skus/seed`,
      skus,
    );
  } catch (error: any) {
    throw new Error(error.response?.data?.error || 'Failed to save seed SKUs');
  }
}

export async function rejectEnrichment(
  distributorId: string,
  sku: string,
): Promise<{ status: string }> {
  try {
    const client = await getAuthenticatedClient();
    const response = await client.post(
      `/distributor/${encodeURIComponent(distributorId)}/enrichments/${encodeURIComponent(sku)}/reject`,
    );
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.error || 'Failed to reject enrichment');
  }
}

/**
 * Regenerate an enrichment for a SKU — useful when the agent's first attempt
 * is poor. Drops the existing row + replaces the blob with a fresh agent run.
 * Returns Status='Draft' (partner reviews the new output before approving).
 * Inline (no queue hop) so the UI can show the new draft immediately.
 */
export async function regenerateEnrichment(
  distributorId: string,
  sku: string,
): Promise<{ status: string; regenerated: boolean }> {
  try {
    const client = await getAuthenticatedClient();
    const response = await client.post(
      `/distributor/${encodeURIComponent(distributorId)}/enrichments/${encodeURIComponent(sku)}/regenerate`,
    );
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.error || 'Failed to regenerate enrichment');
  }
}

// ---------------------------------------------------------------------------
// Catalogue browse — search a distributor's catalogue and return result hits
// the partner can add to their seed list. Backend wraps the connector's
// existing GetCatalogAsync stream + caps at 50 hits per call.
// ---------------------------------------------------------------------------

export interface CatalogSearchHit {
  sku: string;
  vendorPartNumber: string | null;
  vendor: string | null;
  description: string | null;
  category: { l1: string | null; l2: string | null; l3: string | null } | null;
  kind: string | null;
  lifecycle: string | null;
  isAuthorizedToPurchase: boolean | null;
  // Phase 3.10: live price/stock attached server-side via batched
  // GetPriceAndStockAsync after the catalog walk. Either field may be
  // null if Ingram's P&A endpoint was throttled or had no entry.
  price?: number | null;
  rrp?: number | null;
  stock?: number | null;
}

export interface CatalogSearchResponse {
  distributorId: string;
  keyword: string | null;
  keywords?: string[];
  vendor: string | null;
  vendors?: string[];
  vendorPartNumbers?: string[];
  category: string | null;
  type?: string | null;
  categoryFallbackApplied?: boolean;
  inStockOnly?: boolean;
  count: number;
  items: CatalogSearchHit[];
}

export interface CatalogSearchParams {
  keyword?: string;
  keywords?: string[];
  vendor?: string;
  vendors?: string[];
  vendorPartNumbers?: string[];
  category?: string;
  type?: string;
  /** Filter to results with stock > 0. Server fans out P&A then filters. */
  inStock?: boolean;
  includeDiscontinued?: boolean;
  limit?: number;
}

export interface CatalogSearchPostFilters {
  productType?: string | null;
  ramGb?: number | null;
  storageGb?: number | null;
  maxPrice?: number | null;
  screenSize?: string | null;
  processor?: string | null;
  cellular?: boolean | null;
  mustInclude?: string[];
  exclude?: string[];
}

export interface CatalogSearchPlan {
  keywords: string[];
  vendors: string[];
  vendorPartNumbers: string[];
  category: string | null;
  type: string | null;
  inStock?: boolean | null;
  includeDiscontinued: boolean;
  limit: number;
  confidence: number;
  rationale?: string | null;
  postFilters: CatalogSearchPostFilters;
}

export interface CatalogSearchRecommendation {
  distributorId: string;
  rawIntent: string;
  source: 'agent' | 'heuristic' | string;
  search: CatalogSearchPlan;
  warnings: string[];
}

/**
 * Aggregate Active enrichments across multiple distributors. Fans out the
 * per-distributor list endpoint in parallel + tags each row with the
 * source distributor for cross-distributor display. Failures from any
 * single distributor are swallowed (returned as 0 rows for that source)
 * so a misconfigured distributor doesn't block the rest of the view.
 */
export interface AggregatedEnrichmentSummary extends EnrichmentSummary {
  distributorId: string;
}

export async function listEnrichmentsAcrossDistributors(
  distributorIds: string[],
  status: 'Draft' | 'Active' | 'Rejected' = 'Active',
): Promise<{ items: AggregatedEnrichmentSummary[]; failures: Array<{ distributorId: string; message: string }> }> {
  const failures: Array<{ distributorId: string; message: string }> = [];
  const results = await Promise.all(
    distributorIds.map(async (distributorId) => {
      try {
        const response = await listEnrichments(distributorId, status);
        return response.items.map<AggregatedEnrichmentSummary>((item) => ({
          ...item,
          distributorId,
        }));
      } catch (err: any) {
        failures.push({ distributorId, message: err?.message ?? 'Failed to load' });
        return [] as AggregatedEnrichmentSummary[];
      }
    }),
  );
  return { items: results.flat(), failures };
}

/**
 * Single-SKU detail lookup against the live distributor catalogue. Used by
 * the Browse pane so the partner sees real Ingram product info regardless
 * of whether the SKU is in the seed list / has been enriched.
 */
export interface CatalogProductDetail {
  sku: string;
  distributorId: string;
  source: {
    sku: string;
    vendorPartNumber: string | null;
    vendor: string | null;
    shortDescription: string | null;
    displayDescription: string | null;
    category: { l1: string | null; l2: string | null; l3: string | null } | null;
    kind: string | null;
    lifecycle: string | null;
    isAuthorizedToPurchase: boolean | null;
    flags: Record<string, unknown>;
  };
  priceAndStock: EnrichmentLivePriceAndStock | null;
}

export async function getDistributorCatalogProduct(
  distributorId: string,
  sku: string,
): Promise<CatalogProductDetail> {
  try {
    const client = await getAuthenticatedClient();
    const response = await client.get(
      `/distributor/${encodeURIComponent(distributorId)}/catalog/${encodeURIComponent(sku)}`,
    );
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.error || 'Failed to load product detail');
  }
}

export async function searchDistributorCatalog(
  distributorId: string,
  params: CatalogSearchParams,
): Promise<CatalogSearchResponse> {
  const qs = new URLSearchParams();
  if (params.keyword) qs.set('keyword', params.keyword);
  params.keywords?.forEach((keyword) => {
    if (keyword) qs.append('keyword', keyword);
  });
  if (params.vendor) qs.set('vendor', params.vendor);
  params.vendors?.forEach((vendor) => {
    if (vendor) qs.append('vendor', vendor);
  });
  params.vendorPartNumbers?.forEach((vendorPartNumber) => {
    if (vendorPartNumber) qs.append('vendorPartNumber', vendorPartNumber);
  });
  if (params.category) qs.set('category', params.category);
  if (params.type) qs.set('type', params.type);
  if (params.inStock) qs.set('inStock', 'true');
  if (params.includeDiscontinued) qs.set('includeDiscontinued', 'true');
  if (params.limit) qs.set('limit', String(params.limit));
  try {
    const client = await getAuthenticatedClient();
    // Path is /catalog-search (not /catalog/search) so it doesn't collide
    // with the sibling /catalog/{sku} product-detail route in the Azure
    // Functions route table — see comment on SearchDistributorCatalog.
    const response = await client.get(
      `/distributor/${encodeURIComponent(distributorId)}/catalog-search?${qs.toString()}`,
    );
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.error || 'Failed to search catalogue');
  }
}

export async function recommendDistributorCatalogSearch(
  distributorId: string,
  intent: string,
  options?: { inStock?: boolean; limit?: number },
): Promise<CatalogSearchRecommendation> {
  try {
    const client = await getAuthenticatedClient();
    const response = await client.post(
      `/distributor/${encodeURIComponent(distributorId)}/catalog-search/recommend`,
      {
        intent,
        inStock: options?.inStock || undefined,
        limit: options?.limit,
      },
    );
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.error || 'Failed to recommend catalogue search');
  }
}

export interface Pax8PullRecord {
  lastPullAt: string | null;
  currency: string | null;
  licences: number;
  terms: number;
  descriptions: number;
  skippedNoSku: number;
  skippedNotASeat: number;
  skippedUnmapped: number;
  skippedNoPrice: number;
}

export interface Pax8Config {
  connected: boolean;
  status: string | null;
  clientId: string | null;
  clientSecret: string | null;
  lastTestedAt: string | null;
  lastTestResult: string | null;
  lastPull: Pax8PullRecord | null;
}

export interface Pax8ConfigInput {
  clientId?: string;
  clientSecret?: string;
}

export interface Pax8ConnectionTestResult {
  success: boolean;
  latencyMs: number;
  error: string | null;
  message: string;
}

export interface Pax8PricePullResult {
  success: boolean;
  currency: string;
  licences: number;
  terms: number;
  descriptions: number;
  skippedNoSku: number;
  skippedNotASeat: number;
  skippedUnmapped: number;
  skippedNoPrice: number;
  message: string;
}

export async function getPax8Config(): Promise<Pax8Config> {
  try {
    const client = await getAuthenticatedClient();
    const response = await client.get('/distributor/pax8/config');
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.error || 'Failed to load Pax8 configuration');
  }
}

export async function savePax8Config(input: Pax8ConfigInput): Promise<void> {
  try {
    const client = await getAuthenticatedClient();
    await client.put('/distributor/pax8/config', input);
  } catch (error: any) {
    throw new Error(error.response?.data?.error || 'Failed to save Pax8 configuration');
  }
}

export async function testPax8Connection(input: Pax8ConfigInput): Promise<Pax8ConnectionTestResult> {
  try {
    const client = await getAuthenticatedClient();
    const response = await client.post('/distributor/pax8/test', input);
    return response.data;
  } catch (error: any) {
    return {
      success: false,
      latencyMs: 0,
      error: error.response?.data?.error || 'Connection test failed',
      message: 'Connection test failed',
    };
  }
}

export async function pullPax8Prices(): Promise<Pax8PricePullResult> {
  try {
    const client = await getAuthenticatedClient();
    const response = await client.post('/distributor/pax8/pull');
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.error || 'Failed to pull Pax8 prices');
  }
}

export async function deletePax8Config(): Promise<void> {
  try {
    const client = await getAuthenticatedClient();
    await client.delete('/distributor/pax8/config');
  } catch (error: any) {
    throw new Error(error.response?.data?.error || 'Failed to disconnect Pax8');
  }
}
